import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import * as Sentry from '@sentry/react';

import appSettings from '../../app-settings';
import { getNetworkError } from '../../core/utils';

const TAGS_URL = `${appSettings.baseUrl}/tags`;

const SET_ERROR = 'tags/SET_ERROR';
const SET_SUCCESS = 'tags/SET_SUCCESS';
const GET_TAGS = 'tags/GET_TAGS';
const CREATE_TAG = 'tags/CREATE_TAG';
const UPDATE_TAG = 'tags/UPDATE_TAG';
const DELETE_TAG = 'tags/DELETE_TAG';
const GET_TAG_CONTACT_COUNTS = 'tags/GET_TAG_CONTACT_COUNTS';

export const setError = createAction(SET_ERROR);

export const setSuccess = createAction(SET_SUCCESS);

export const getTags = createAsyncThunk(GET_TAGS, async () => {
  const res = await axios.get(TAGS_URL);
  return { tags: res.data.tags };
});

// `description` is REQUIRED by lists-api: it is the audience context handed to
// content generation. Callers must use TagFormModal rather than sending a bare name.
export const createTag = createAsyncThunk(CREATE_TAG, async ({ name, description }) => {
  try {
    const res = await axios.post(TAGS_URL, { name, description });
    return res.data;
  } catch (err) {
    Sentry.captureException(err);
    const errorMessage = getNetworkError(err);
    throw new Error(errorMessage);
  }
});

// PATCH is a genuine partial update server-side: only the keys sent are written.
// Reserved tags accept `description` but reject `name` with a 403, so callers must
// omit the name for those rather than sending it unchanged.
export const updateTag = createAsyncThunk(UPDATE_TAG, async ({ tagId, name, description }) => {
  try {
    const patch = {};
    if (name !== undefined) patch.name = name;
    if (description !== undefined) patch.description = description;
    await axios.patch(`${TAGS_URL}/${tagId}`, patch);
  } catch (err) {
    Sentry.captureException(err);
    const errorMessage = getNetworkError(err);
    throw new Error(errorMessage);
  }
});

/**
 * Contact counts for specific tags, as { [tagId]: count }.
 *
 * lists-api exposes only a per-tag count endpoint, so this fans out. Fired in
 * parallel and tolerant of individual failures: a count that cannot be read shows
 * as unknown rather than failing the whole onboarding step.
 */
export const getTagContactCounts = createAsyncThunk(GET_TAG_CONTACT_COUNTS, async (tagIds) => {
  const results = await Promise.all(
    tagIds.map(async (tagId) => {
      try {
        const res = await axios.get(`${TAGS_URL}/${tagId}/contacts/count`);
        return [tagId, res.data.count];
      } catch (err) {
        Sentry.captureException(err);
        return [tagId, null];
      }
    }),
  );

  return Object.fromEntries(results);
});

export const deleteTag = createAsyncThunk(DELETE_TAG, async (tagId) => {
  try {
    await axios.delete(`${TAGS_URL}/${tagId}`);
  } catch (err) {
    Sentry.captureException(err);
    const errorMessage = getNetworkError(err);
    throw new Error(errorMessage);
  }
});
